import React, {useEffect} from 'react';
import {connect} from 'react-redux';
import {useForm, useFormState} from 'react-final-form';
import {Card, Grid} from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';

import {Dispatch, SelectOption, Store} from 'spectra-logic-ui';
import {fetchResource} from 'spectra-logic-ui/actions';
import {WizardPage} from 'spectra-logic-ui/components';

import {FormActionType, StorageClass, TargetType} from '@/enum';
import {Bucket, Pool, Target, TargetItem, TargetItemDetails} from '@/types';
import Checkbox from '@/components/form/checkbox';
import DialogDescription from '@/components/form/dialog_description';
import FormSingleSelect from '@/components/form/single_select';
import TextField from '@/components/form/text_field';
import {isArchivalSupported, isStorageClassArchival} from '@/storage/archive';
import {cloneRestoreTooltip, recoverableTooltip} from '@/storage/details/properties';
import {items, itemDetails} from '@/storage/form/store';
import DisabledCard from '@/storage/form/pages/disabled_card';
import {BpLinkTooltip} from '@/storage/form/pages/onsite/tooltips';
import OptionalDataCard from '@/storage/form/pages/onsite/optional_data_card';
import WriteLimitCard from '@/storage/form/pages/onsite/write_limit_card';
import QuotaCard, {ItemDetails} from '@/storage/form/pages/onsite/quota_card';
import ThresholdsCard from '@/storage/form/pages/onsite/thresholds_card';
import FormMultiSelect from '@/components/form/multi_select';
import {UseWithTooltip} from '@/storage/form/pages/tooltips';

type ParametersCardProps = {
  action: FormActionType;
  target?: TargetType;
  targetTypes?: Target[];
  buckets: Bucket[];
  bucketsFetching: boolean;
  targetItems: TargetItem[];
  storageClass: StorageClass;
  itemDetails?: TargetItemDetails;
  itemFetching: boolean;
  error?: string;
  importer?: boolean;
  useWith?: string[];
  pool?: Pool;
}

type PageProps = {
  title: string;
  action: FormActionType;
  endpoint: string;
  buckets?: Bucket[];
  bucketsFetching?: boolean;
  targetTypes?: Target[];
  targetItems?: TargetItem[];
  fetchBuckets?: Function;
  fetchTargetTypes?: Function;
  fetchDS3Buckets?: Function;
  fetchItemDetails?: Function;
  pool?: Pool;
  itemDetails?: TargetItemDetails;
  itemFetching?: boolean;
  itemErrorMessage?: string;
  validate?: (values: any) => object;
}

const useStyles = makeStyles({
  root: {
    padding: 15,
    marginTop: 10,
  },
  grid: {
    '& > *': {
      marginBottom: 10,
    },
  },
});

const ParametersCard = ({buckets = [], targetItems = [], action, target, targetTypes,
  pool, storageClass, itemDetails, itemFetching, bucketsFetching, error=''}: ParametersCardProps) => {
  const classes = useStyles();
  let bpBucketOptions;
  if (action === FormActionType.EDIT && pool && pool.item) {
    bpBucketOptions = [{key: pool.item, text: pool.item}];
  } else {
    bpBucketOptions = targetItems.map((b) => ({key: b.id, text: b.name ? b.name : b.id})) as SelectOption[];
  }

  const bucketOptions = buckets.map((b) => {
    return {key: b.name, text: b.name};
  }) as SelectOption[];

  const stgClasses = itemDetails && itemDetails.classes ? itemDetails.classes : [];

  const form = useForm();
  const disableCloneRestoreForNonArchival = (e: any) => {
    if (e && e.target && !isStorageClassArchival(e.target.value)) {
      form.mutators.setFormValue('restoreInPlace', false);
    }
  };

  const clearStorageClass = () => {
    form.mutators.setFormValue('storageClass', undefined);
  };

  const targetInfo = targetTypes ? targetTypes.find((t) => t.id === target) : undefined;
  const sLabel = targetInfo ? targetInfo.name : '';
  const selectorLabel = action === FormActionType.EDIT ? sLabel : 'Select ' + sLabel;
  const itemTooltip = targetInfo ? targetInfo.tip : undefined;
  return (
    <Card className={classes.root}>
      {error && <p className='error'>{error}</p>}
      <Grid container spacing={2} className={classes.grid}>
        <Grid item xs={6}>
          <FormSingleSelect
            name='item' label={selectorLabel} tooltip={itemTooltip}
            options={bpBucketOptions} disabled={action === FormActionType.EDIT}
            onChange={clearStorageClass}
          />
        </Grid>
        <Grid item xs={6}>
          <TextField name='name' label='Storage Name' />
        </Grid>
        <Grid item xs={6}>
          <FormSingleSelect
            name='storageClass' label='Select Storage Class'
            options={stgClasses} disabled={stgClasses.length === 0 || itemFetching}
            onChange={disableCloneRestoreForNonArchival}
          />
        </Grid>
        <Grid item xs={12}>
          <FormMultiSelect
            name='useWith' label='Use With Buckets'
            tooltip={target === TargetType.BPLINK ? < BpLinkTooltip/> : < UseWithTooltip/>}
            noneOption='All buckets' disabled={bucketsFetching}
            options={bucketOptions}
          />
        </Grid>
        {target !== TargetType.BPLINK && <Grid item xs={6}>
          <Checkbox name='recoverable' label='Third-party Recovery' tooltip={recoverableTooltip} />
        </Grid>}
        {isArchivalSupported(stgClasses) &&
          <Grid item xs={6}>
            <Checkbox
              name='restoreInPlace' label='Restore In-place' tooltip={cloneRestoreTooltip}
              disabled={!isStorageClassArchival(storageClass)}
            />
          </Grid>
        }
      </Grid>
    </Card>
  );
};


const OnSiteParametersPage = (props: PageProps) => {
  const {title, action, buckets = [], targetTypes = [], targetItems = [], fetchBuckets,
    bucketsFetching = false, pool, fetchItemDetails, fetchTargetTypes,
    itemDetails, itemFetching=false, itemErrorMessage, ...otherProps} = props;

  const form = useForm();
  const formState = useFormState();
  const item = formState.values.item;
  const target = formState.values.target;
  const useWith = formState.values.useWith;
  const storageClass = formState.values.storageClass;
  const stgClasses = itemDetails && itemDetails.classes ? itemDetails.classes : [];

  useEffect(() => {
    if (fetchBuckets !== undefined) {
      fetchBuckets();
    }
  }, []);

  useEffect(() => {
    if (fetchTargetTypes !== undefined) {
      fetchTargetTypes();
    }
  }, []);

  if (action === FormActionType.CREATE && fetchItemDetails) {
    useEffect(() => {
      if (item) {
        fetchItemDetails(formState.values);
      }
    }, [item]);

    useEffect(() => {
      if (storageClass && !stgClasses.includes(storageClass)) {
        form.mutators.setFormValue('storageClass', undefined);
      }
    }, [itemDetails]);
  }

  useEffect(() => {
    if (target === TargetType.BPLINK) {
      form.mutators.setFormValue('recoverable', undefined);
    }
  }, [target]);

  return (
    <WizardPage
      title={title} {...otherProps}
      description={<DialogDescription>Configure your storage below.</DialogDescription>}
    >
      <ParametersCard
        buckets={buckets}
        bucketsFetching={bucketsFetching}
        targetItems={targetItems}
        targetTypes={targetTypes}
        target={target}
        action={action}
        pool={pool}
        storageClass={storageClass}
        itemDetails={itemDetails}
        itemFetching={itemFetching}
        error={itemErrorMessage}
        useWith={useWith}
      />
      {itemDetails && itemDetails.threshold && <ThresholdsCard />}
      {itemDetails && itemDetails.optional && <OptionalDataCard />}
      {itemDetails && itemDetails.writeLimit && <WriteLimitCard />}
      {itemDetails && itemDetails.quota && <QuotaCard itemDetails={itemDetails.properties as ItemDetails} />}
      <DisabledCard />
    </WizardPage>
  );
};

const mapStateToProps = (state: Store) => {
  const bucketsResource = state.resources['buckets'] || {};
  const targetItemsResource = state.resources[items] || {};
  const itemState = state.resources[itemDetails] || {};
  const targetTypesResource = state.resources['targets'] || {};
  return {
    buckets: bucketsResource.data,
    bucketsFetching: bucketsResource.fetching,
    targetItems: targetItemsResource.data,
    itemDetails: itemState.data,
    itemFetching: itemState.fetching,
    itemErrorMessage: itemState.errorMessage,
    targetTypes: targetTypesResource.data,
  };
};

const mapDispatchToProps = (dispatch: Dispatch, {endpoint, pool = {} as Pool}: PageProps) => ({
  fetchBuckets: () => dispatch(fetchResource('buckets')),
  fetchItemDetails: (values: any) => {
    const opts = {storeName: itemDetails};
    if (pool && pool.id) {
      return dispatch(fetchResource(`storage/${pool.id}/item`, '', opts));
    } else {
      return dispatch(fetchResource(`targets/${endpoint}/${values.target}/${values.item}`, '', opts));
    }
  },
  fetchTargetTypes: () => dispatch(fetchResource('targets')),
});

export default connect(mapStateToProps, mapDispatchToProps)(OnSiteParametersPage);
